import React, { useCallback, useEffect, useRef, useState } from "react";
import "./riders.css";
import ReactPaginate, { ReactPaginateProps } from "react-paginate";
import "react-toastify/dist/ReactToastify.css";
import { useDisclosure } from "@chakra-ui/hooks";
import {
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
} from "@chakra-ui/modal";
import { Button, ChakraProvider } from "@chakra-ui/react";
import deleteIcon from "../../../assets/svg/deleteIcon.svg";
import blockIcon from "../../../assets/svg/blockIcon.svg";
import {
  getAllRidersApi,
  handleRiderDeleteApi,
  searchRidersApi,
  updateRiderStatusApi,
} from "../../../services/customAPI";
import ColumnsTableRiders from "./components/ColumnsTableRiders";
import Loader from "components/loader/loader";
import Navbar from "../../../components/navbar";
import Card from "components/card";
import { t } from "i18next";
import { DTO, Types } from "shared-lib";
import { errorToast, successToast } from "utils/commons";
import { useSearchParams } from "react-router-dom";

const Riders: React.FC = () => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isOpen: isViewOpen, onOpen: onViewOpen, onClose: onViewClose } = useDisclosure();
  const limit = 10;
  const currentPage = useRef<number>(1);
  const [selectedItem, setSelectedItem] = useState<DTO.RiderDTO | null>(null);
  const [viewItem, setViewItem] = useState<DTO.RiderDTO | null>(null);
  const [modalState, setModalState] = useState(true);
  const [pageCount, setPageCount] = useState<number>(1);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [paginatedRiders, setPaginatedRiders] = useState<DTO.RiderDTO[]>([]);
  const [pageItemStartNumber, setPageItemStartNumber] = useState<number>(1);
  const [pageItemEndNumber, setPageItemEndNumber] = useState<number>(limit);
  const [searchParams, setSearchParams] = useSearchParams();

  // Initialize search text from URL
  const initialSearch = searchParams.get("search") || "";
  const [searchText, setSearchText] = useState<string>(initialSearch);

  const firstRender = useRef(true);

  // Sync search text with URL params
  useEffect(() => {
    if (searchText) {
      setSearchParams({ search: searchText });
    } else {
      setSearchParams({});
    }
  }, [searchText, setSearchParams]);

  const setPageItemRange = (
    currPageNumber: number,
    maxItemRange: number
  ): void => {
    let startNumber = currPageNumber * limit - limit + 1;
    if (startNumber < 0) {
      startNumber = 0;
    }
    setPageItemStartNumber(startNumber);

    const endNumber = currPageNumber * limit;
    setPageItemEndNumber(Math.min(endNumber, maxItemRange));
  };

  const searchRiders = useCallback(async (): Promise<DTO.RiderSearchResponseDTO | null> => {
    try {
      setIsLoading(true);
      setErrorMessage("");
      const response = await searchRidersApi({
        page: currentPage.current,
        limit: limit,
        query: searchText.trim(),
      });
      return response;
    } catch (error) {
      setPaginatedRiders([]);
      setPageCount(1);
      setIsLoading(false);
      setErrorMessage(t("Erro ao buscar passageiros"));
      return null;
    }
  }, [limit, searchText]);

  const searchRiderFunction = useCallback(async (): Promise<void> => {
    try {
      const response = await searchRiders();
      if (!response) {
        return;
      }
      setPageCount(Math.ceil((response.total ?? 0) / limit));
      setPaginatedRiders(response.data ?? []);
      setPageItemRange(currentPage.current, response.total ?? 0);
    } catch (error) {
      console.error("search rider error:", error);
      setErrorMessage(t("Erro ao buscar passageiros"));
    } finally {
      setIsLoading(false);
    }
  }, [limit, searchRiders]);

  const handleSearchSubmit = async (e: React.SyntheticEvent<EventTarget>) => {
    e.preventDefault();
    if (searchText.trim() === "") {
      return;
    }
    currentPage.current = 1;
    searchRiderFunction();
  };

  const updateRiderStatus = async (
    id: number,
    currentStatus: Types.RiderStatusEnum,
  ) => {
    const nextStatus =
      currentStatus === Types.RiderStatusEnum.ACTIVE
        ? Types.RiderStatusEnum.INACTIVE
        : Types.RiderStatusEnum.ACTIVE;
    try {
      setIsLoading(true);
      setErrorMessage("");
      onClose();
      const response = await updateRiderStatusApi({
        id,
        status: nextStatus,
      });
      if (response.data?.success) {
        successToast(t("Status atualizado com sucesso"));
        getAllRiders();
      } else {
        errorToast(t("Alguma coisa deu errado!"));
      }
    } catch (error) {
      errorToast(t("Alguma coisa deu errado!"));
      setErrorMessage(t("Erro ao atualizar status do passageiro"));
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    onClose();
    try {
      setIsLoading(true);
      setErrorMessage("");
      const response = await handleRiderDeleteApi(id);
      if (response.data?.success) {
        successToast(t("Passageiro deletado com sucesso!"));
        const updatedRidersData = paginatedRiders.filter(
          (rider) => rider.id !== id
        );

        setPageCount(Math.ceil(updatedRidersData.length / limit));
        currentPage.current = 1;
        setPaginatedRiders(updatedRidersData);
        if (updatedRidersData.length % limit === 0) {
          currentPage.current = currentPage.current - 1;
          setPaginatedRiders(updatedRidersData);
        }

        setPageItemRange(currentPage.current, updatedRidersData.length);
        getAllRiders();
      } else {
        errorToast(t("Alguma coisa deu errado!"));
      }
    } catch (error) {
      errorToast(t("Alguma coisa deu errado!"));
      setErrorMessage(t("Erro ao excluir passageiro"));
    } finally {
      setIsLoading(false);
    }
  };

  const handleClickForDeleteModal = (data: DTO.RiderDTO) => {
    setIsLoading(true);
    setModalState(true);
    setSelectedItem(data);
    onOpen();
    setIsLoading(false);
  };

  const handleToggleForStatusModal = (data: DTO.RiderDTO) => {
    setIsLoading(true);
    setModalState(false);
    setSelectedItem(data);
    onOpen();
    setIsLoading(false);
  };

  const handleViewRider = (data: DTO.RiderDTO) => {
    setViewItem(data);
    onViewOpen();
  };

  // Pagination
  const handlePageClick: ReactPaginateProps["onPageChange"] = (
    event,
  ) => {
    currentPage.current = event.selected + 1;
    if (searchText !== "") {
      searchRiderFunction();
    } else {
      getAllRiders();
    }
  };

  const getAllRiders = useCallback(async (): Promise<void> => {
    try {
      setIsLoading(true);
      setErrorMessage("");
      const response = await getAllRidersApi({
        page: currentPage.current,
        limit: limit,
      });
      setPageCount(Math.ceil((response.totalRiders ?? 0) / limit));
      setPaginatedRiders(response.data ?? []);
      setPageItemRange(currentPage.current, response.totalRiders ?? 0);
    } catch (error) {
      errorToast(t("Alguma coisa deu errado!"));
      setErrorMessage(t("Erro ao carregar passageiros"));
    } finally {
      setIsLoading(false);
    }
  }, [limit]);

  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
    } else {
      if (searchText.trim() === "") {
        currentPage.current = 1;
        getAllRiders();
      }
    }
  }, [getAllRiders, searchText]);
  useEffect(() => {
    currentPage.current = 1;
    if (searchText.trim() !== '') {
      searchRiderFunction();
    } else {
      getAllRiders();
    }
  }, [getAllRiders]);

  return (
    <div>
      <Navbar
        flag={true}
        brandText={t("Riders")}
        handleSearch={(e: React.SyntheticEvent<EventTarget>) =>
          handleSearchSubmit(e)
        }
        setSearchText={setSearchText}
      />
      {errorMessage ? (
        <Card extra="w-full mt-4 p-4 h-full">
          <div className="text-sm text-red-400">{errorMessage}</div>
        </Card>
      ) : null}
      {isLoading ? (
        <Loader />
      ) : (
        <>
          {
            <>
              <div className="m-4">
                <ColumnsTableRiders
                  tableData={paginatedRiders}
                  handleClickForDeleteModal={handleClickForDeleteModal}
                  handleToggleForStatusModal={handleToggleForStatusModal}
                  handleViewRider={handleViewRider}
                />
                <div
                  className="mx-2 "
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    justifyContent: "space-between",
                    color: "#5E6278",
                  }}
                >
                  <h5>
                    {pageItemStartNumber} - {pageItemEndNumber}
                  </h5>
                  <div style={{ marginTop: "1rem" }}>
                    <ReactPaginate
                      breakLabel=" .  .  . "
                      nextLabel=">"
                      onPageChange={handlePageClick}
                      pageRangeDisplayed={3}
                      pageCount={pageCount}
                      previousLabel="<"
                      renderOnZeroPageCount={null}
                      marginPagesDisplayed={2}
                      containerClassName="pagination"
                      pageClassName="page-item"
                      pageLinkClassName="page-link"
                      previousClassName="previous-page-btn"
                      previousLinkClassName="page-link"
                      nextClassName="next-page-btn"
                      nextLinkClassName="page-link"
                      activeClassName="active"
                      forcePage={currentPage.current - 1}
                    />
                  </div>
                </div>
              </div>
            </>
            // )
          }
          {isOpen && (
            <ChakraProvider>
              <Modal
                isCentered={true}
                isOpen={isOpen}
                onClose={onClose}
                size="xs"
              >
                <ModalOverlay />
                <ModalContent>
                  <ModalHeader></ModalHeader>
                  {/* <ModalCloseButton /> */}
                  <div className="mb-2 flex justify-center">
                    {modalState ? (
                      <img src={deleteIcon} alt={t("delete")} />
                    ) : (
                      <img src={blockIcon} alt={t("Block")} />
                    )}
                  </div>
                  {modalState ? (
                    <ModalBody className="text-center">
                      {t("Are you sure you want to Delete?")} <br />
                      {'"' + (selectedItem?.firstName ?? "") + '"'}
                    </ModalBody>
                  ) : (
                    <ModalBody className="text-center">
                      {selectedItem?.status === Types.RiderStatusEnum.ACTIVE
                        ? t("Are you sure you want to Block ?")
                        : t("Are you sure you want to Unblock ?")}
                      <br />
                      {'"' + (selectedItem?.firstName ?? "") + '"'}
                    </ModalBody>
                  )}

                  <div className="mt-3 flex justify-center">
                    {modalState ? (
                      <Button
                        className="cancel-delete-modal-button mx-2"
                        onClick={onClose}
                      >
                        {t("cancel")}
                      </Button>
                    ) : (
                      <Button
                        className="cancel-block-modal-button mx-2"
                        onClick={onClose}
                      >
                        {t("cancel")}
                      </Button>
                    )}
                    {modalState ? (
                      <Button
                        className="delete-modal-button mx-2"
                        onClick={() => handleDelete(selectedItem?.id ?? 0)}
                      >
                        {t("delete")}
                      </Button>
                    ) : (
                      <Button
                        className="block-modal-button mx-2"
                        onClick={() => {
                          const currentStatus =
                            selectedItem?.status ??
                            Types.RiderStatusEnum.INACTIVE;
                          updateRiderStatus(selectedItem?.id ?? 0, currentStatus);
                        }}
                      >
                        {selectedItem?.status === Types.RiderStatusEnum.ACTIVE
                          ? t("Block")
                          : t("Unblock")}
                      </Button>
                    )}
                  </div>
                  <ModalFooter></ModalFooter>
                </ModalContent>
              </Modal>
            </ChakraProvider>
          )}

          {/* View Rider Modal */}
          {isViewOpen && viewItem && (
            <ChakraProvider>
              <Modal isCentered isOpen={isViewOpen} onClose={onViewClose} size="md">
                <ModalOverlay />
                <ModalContent style={{ background: "#1B254B", color: "#fff" }}>
                  <ModalHeader style={{ textAlign: "center", borderBottom: "1px solid rgba(255,255,255,0.1)", paddingBottom: "12px" }}>
                    {t("Detalhes do Passageiro")}
                  </ModalHeader>
                  <ModalBody>
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "16px", padding: "16px 0" }}>
                      {/* Profile Image */}
                      {viewItem.profileImageKey ? (
                        <img
                          src={`${process.env.REACT_APP_BASE_URL}/uploads/${viewItem.profileImageKey}`}
                          alt="Profile"
                          style={{ width: "80px", height: "80px", borderRadius: "50%", objectFit: "cover", border: "3px solid rgba(255,255,255,0.2)" }}
                        />
                      ) : (
                        <div style={{ width: "80px", height: "80px", borderRadius: "50%", backgroundColor: "rgba(255,255,255,0.1)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "32px", color: "#a0aec0" }}>
                          {viewItem.firstName?.charAt(0)?.toUpperCase() || "?"}
                        </div>
                      )}

                      {/* Info Grid */}
                      <div style={{ width: "100%", display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
                        <div style={{ background: "rgba(255,255,255,0.05)", borderRadius: "8px", padding: "12px" }}>
                          <p style={{ fontSize: "11px", color: "#a0aec0", marginBottom: "4px" }}>{t("Name")}</p>
                          <p style={{ fontSize: "14px", fontWeight: 600 }}>{viewItem.firstName || "N/D"} {viewItem.lastName || ""}</p>
                        </div>
                        <div style={{ background: "rgba(255,255,255,0.05)", borderRadius: "8px", padding: "12px" }}>
                          <p style={{ fontSize: "11px", color: "#a0aec0", marginBottom: "4px" }}>{t("Mobile Number")}</p>
                          <p style={{ fontSize: "14px", fontWeight: 600 }}>{viewItem.mobileNumber || "N/D"}</p>
                        </div>
                        <div style={{ background: "rgba(255,255,255,0.05)", borderRadius: "8px", padding: "12px" }}>
                          <p style={{ fontSize: "11px", color: "#a0aec0", marginBottom: "4px" }}>{t("Email")}</p>
                          <p style={{ fontSize: "14px", fontWeight: 600 }}>{viewItem.email || "N/D"}</p>
                        </div>
                        <div style={{ background: "rgba(255,255,255,0.05)", borderRadius: "8px", padding: "12px" }}>
                          <p style={{ fontSize: "11px", color: "#a0aec0", marginBottom: "4px" }}>{t("Status")}</p>
                          <p style={{ fontSize: "14px", fontWeight: 600, color: viewItem.status === Types.RiderStatusEnum.ACTIVE ? "#48bb78" : "#fc5185" }}>
                            {viewItem.status === Types.RiderStatusEnum.ACTIVE ? t("Active") : t("Inactive")}
                          </p>
                        </div>
                        <div style={{ background: "rgba(255,255,255,0.05)", borderRadius: "8px", padding: "12px" }}>
                          <p style={{ fontSize: "11px", color: "#a0aec0", marginBottom: "4px" }}>{t("Total Rides Completed")}</p>
                          <p style={{ fontSize: "14px", fontWeight: 600 }}>{viewItem.totalRidesCompleted ?? 0}</p>
                        </div>
                        <div style={{ background: "rgba(255,255,255,0.05)", borderRadius: "8px", padding: "12px" }}>
                          <p style={{ fontSize: "11px", color: "#a0aec0", marginBottom: "4px" }}>{t("Average Rating")}</p>
                          <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                            <p style={{ fontSize: "14px", fontWeight: 600, color: "#ecc94b" }}>{viewItem.averageRating != null ? Number(viewItem.averageRating).toFixed(1) : "N/D"}</p>
                            <span style={{ color: "#ecc94b", fontSize: "14px" }}>★</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </ModalBody>
                  <ModalFooter style={{ borderTop: "1px solid rgba(255,255,255,0.1)", justifyContent: "center" }}>
                    <Button
                      className="cancel-block-modal-button"
                      onClick={onViewClose}
                    >
                      {t("Fechar")}
                    </Button>
                  </ModalFooter>
                </ModalContent>
              </Modal>
            </ChakraProvider>
          )}
        </>
      )}
    </div>
  );
};

export default Riders;
